import { type KeyboardEvent, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import type { Asset } from "../types";

/**
 * Click/keyboard "open source chat" interaction shared by AssetCard and AssetGridTile.
 * Spread the returned props onto the clickable root; `openable` is false when the asset
 * has no source chat (`chatUuid == null`) — the row stays inert.
 */
export function useAssetOpenProps(asset: Asset) {
    const navigate = useNavigate();
    const openable = asset.chatUuid != null;

    const open = useCallback(() => {
        if (asset.chatUuid) void navigate(`/chat/${asset.chatUuid}`);
    }, [asset.chatUuid, navigate]);

    return {
        openable,
        role: "button" as const,
        tabIndex: openable ? 0 : -1,
        onClick: open,
        onKeyDown: (e: KeyboardEvent) => {
            if (openable && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                open();
            }
        },
    };
}

/**
 * Навигация в чат-источник ассета как самостоятельный колбэк (для hover-иконки тайла
 * грида, где основной клик уходит в лайтбокс, а не в чат). No-op при `chatUuid == null`.
 */
export function useOpenAssetChat() {
    const navigate = useNavigate();
    return useCallback(
        (asset: Asset) => {
            if (asset.chatUuid) void navigate(`/chat/${asset.chatUuid}`);
        },
        [navigate],
    );
}
